// The browser half of Web Push (JAR-2389): asking permission, this device's
// subscription, and telling core about it.

import { deletePushSubscription, getPushKey, isPushNotConfigured, savePushSubscription } from '@/app/api/push';

/** Whether this browser can show push notifications. An iPhone can only from a
 *  web app on its Home Screen, and the app sends nobody there (JAR-2464). */
export type PushSupport = 'supported' | 'iphone-browser' | 'unsupported';

export function pushSupport(): PushSupport {
  if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) return 'supported';
  return /iPhone|iPod/.test(navigator.userAgent) ? 'iphone-browser' : 'unsupported';
}

export type PushOutcome = 'on' | 'denied' | 'not-configured' | 'failed';

/** Where a tap on a push opens the app when no JarvisTravel window is open, and
 *  the message an open one gets instead. public/push-sw.js uses both. */
export const BELL_PATH = '/notifications';
export const OPEN_BELL_MESSAGE = 'jarvis:open-bell';

/** Turns push on for this device, answering with an outcome on every path. Call
 *  it straight from the tap: Safari asks for permission only inside the gesture,
 *  so the request comes before any await. */
export async function turnOnPush(): Promise<PushOutcome> {
  try {
    const permission = Notification.requestPermission();
    if ((await permission) !== 'granted') return 'denied';
    // `ready` never settles on a device with no worker registered, so that one
    // answers here instead of waiting forever.
    if (!(await navigator.serviceWorker.getRegistration())) return 'failed';
    const key = await getPushKey();
    const registration = await navigator.serviceWorker.ready;
    // ponytail: an existing subscription is reused whatever key made it. A VAPID
    // rotation invalidates every subscription anyway, and needs its own migration.
    const subscription = (await registration.pushManager.getSubscription())
      ?? (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: fromBase64Url(key) }));
    try {
      await savePushSubscription(subscription.toJSON());
    } catch (error) {
      // Unsubscribed, so this device never reads as on while core can't reach it.
      // An unsubscribe that fails too doesn't replace why the save failed.
      await subscription.unsubscribe().catch(() => false);
      throw error;
    }
    return 'on';
  } catch (error) {
    return isPushNotConfigured(error) ? 'not-configured' : 'failed';
  }
}

/** Turns push off for this device, and never rejects. Push is per device, so it
 *  answers whether this one is off: true once the browser has dropped its
 *  subscription, and false when the browser won't, since a subscription it keeps
 *  still gets pushes and is saved again at the next start. Core is told too, best
 *  effort: core prunes a dropped subscription on its next send anyway. */
export async function turnOffPush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    const subscription = await registration?.pushManager?.getSubscription();
    if (!subscription) return true;
    const { endpoint } = subscription;
    if (!(await subscription.unsubscribe())) return false;
    await deletePushSubscription(endpoint).catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}

/** Saves this device's subscription to core again. The push service can replace a
 *  subscription while the app is closed (public/push-sw.js subscribes again), and
 *  the worker can't sign in to tell core, so a signed-in start does. Core keys a
 *  save on its endpoint, so a repeat changes nothing. Best effort: the next start
 *  tries again. */
// ponytail: one save per signed-in start; keep the last saved endpoint and compare
// if that request ever shows up in load.
export async function resaveThisDevice(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    const subscription = await registration?.pushManager?.getSubscription();
    if (subscription) await savePushSubscription(subscription.toJSON());
  } catch {
    // The next signed-in start tries again.
  }
}

/** Whether this device has a push subscription. One that can't say reads as off,
 *  and a tap subscribes it. */
export async function isThisDeviceSubscribed(): Promise<boolean> {
  if (pushSupport() !== 'supported') return false;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    return Boolean(await registration?.pushManager.getSubscription());
  } catch {
    return false;
  }
}

/** A VAPID key, base64url as core serves it, as the bytes subscribe() takes. */
export function fromBase64Url(key: string): Uint8Array<ArrayBuffer> {
  const base64 = key.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(key.length / 4) * 4, '=');
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}
